import { useRef } from "react";
import { Box, IconButton, Paper, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import TopList from "../../../components/TopList/top-list.component";
import type { LocationUsage } from "../../Reports/typings/reports.types";
import { topListPaperSx } from "../dashboard.styles";
import { useTopListCarouselTrack } from "./useTopListCarouselTrack";

type DashboardTopListCarouselProps = {
  locations: LocationUsage[];
  mobileWorkingEnabled: boolean;
  bookingAssistenceActive: boolean;
  setIsQuickAutomatedPlaceBooking: (value: boolean) => void;
};

/** Active slide width — ~18% of the track peeks the next card (stable across widths). */
const PEEK_SLIDE_BASIS = "82%";

/** Keep in sync with track `gap`. */
const PEEK_GAP_PX = 12;

const locationKey = (location: LocationUsage): string =>
  `${location.locationInventoryId}-${location.locationName}`;

const getSlideScrollStepPx = (track: HTMLDivElement): number => {
  const slide = track.querySelector<HTMLElement>("[data-testid='dashboard-top-list-peek-slide']");
  if (slide) {
    return slide.offsetWidth + PEEK_GAP_PX;
  }

  return track.clientWidth * 0.82 + PEEK_GAP_PX;
};

const trackSx = (isDragging: boolean): SxProps<Theme> => ({
  display: "flex",
  flexDirection: "row",
  gap: `${PEEK_GAP_PX}px`,
  width: "100%",
  minWidth: 0,
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  scrollPaddingInline: 0,
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  cursor: isDragging ? "grabbing" : "grab",
  touchAction: "pan-x",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  "& a, & button": {
    cursor: "pointer"
  }
});

const slideSx: SxProps<Theme> = {
  flex: `0 0 ${PEEK_SLIDE_BASIS}`,
  maxWidth: PEEK_SLIDE_BASIS,
  minWidth: 0,
  scrollSnapAlign: "start",
  scrollSnapStop: "always"
};

const stageSx = (theme: Theme, canScrollLeft: boolean, canScrollRight: boolean): SxProps<Theme> => {
  const insetShadows: string[] = [];
  if (canScrollLeft) {
    insetShadows.push(
      `inset 16px 0 20px -12px ${alpha(
        theme.palette.common.black,
        theme.palette.mode === "dark" ? 0.5 : 0.16
      )}`
    );
  }
  if (canScrollRight) {
    insetShadows.push(
      `inset -16px 0 20px -12px ${alpha(
        theme.palette.common.black,
        theme.palette.mode === "dark" ? 0.5 : 0.16
      )}`
    );
  }

  return {
    position: "relative",
    width: "100%",
    minWidth: 0,
    mb: 2,
    boxShadow: insetShadows.length > 0 ? insetShadows.join(", ") : "none",
    ...(canScrollLeft && {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: "14%",
        maxWidth: 48,
        pointerEvents: "none",
        zIndex: 1,
        background: `linear-gradient(to left, transparent 0%, ${alpha(theme.palette.background.default, 0.45)} 50%, ${theme.palette.background.default} 100%)`
      }
    }),
    ...(canScrollRight && {
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: "14%",
        maxWidth: 48,
        pointerEvents: "none",
        zIndex: 1,
        background: `linear-gradient(to right, transparent 0%, ${alpha(theme.palette.background.default, 0.45)} 50%, ${theme.palette.background.default} 100%)`
      }
    })
  };
};

const chevronButtonSx = (
  theme: Theme,
  edge: "previous" | "next",
  visible: boolean
): SxProps<Theme> => ({
  position: "absolute",
  ...(edge === "previous" ? { left: 6 } : { right: 6 }),
  top: "50%",
  zIndex: 2,
  width: 28,
  height: 28,
  p: 0,
  transform: "translateY(-50%)",
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? "auto" : "none",
  bgcolor: alpha(theme.palette.background.paper, 0.92),
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(["opacity", "background-color", "box-shadow"], {
    duration: theme.transitions.duration.short
  }),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none"
  },
  "&&:hover": {
    bgcolor: theme.palette.background.paper,
    boxShadow: theme.shadows[2]
  }
});

/**
 * Peek TopList carousel: grab-drag, peek + edge fade, clickable left/right chevrons.
 */
export const DashboardTopListCarousel = ({
  locations,
  mobileWorkingEnabled,
  bookingAssistenceActive,
  setIsQuickAutomatedPlaceBooking
}: DashboardTopListCarouselProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const hasMultiple = locations.length > 1;
  const { canScrollLeft, canScrollRight, isDragging, onTrackScroll } = useTopListCarouselTrack(
    trackRef,
    hasMultiple,
    locations.length
  );

  const scrollBySlide = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    track.scrollBy({
      left: direction * getSlideScrollStepPx(track),
      behavior: "smooth"
    });
  };

  if (locations.length === 0) {
    return null;
  }

  return (
    <Box
      sx={stageSx(theme, canScrollLeft, canScrollRight)}
      data-testid="dashboard-top-list-carousel"
      data-can-scroll-left={canScrollLeft ? "true" : "false"}
      data-can-scroll-right={canScrollRight ? "true" : "false"}
    >
      <Box
        ref={trackRef}
        sx={trackSx(isDragging)}
        role="region"
        aria-label={t("_dashboard_topLocationsCarousel")}
        onScroll={onTrackScroll}
      >
        {locations.map(location => (
          <Box key={locationKey(location)} sx={slideSx} data-testid="dashboard-top-list-peek-slide">
            <Paper variant="outlined" sx={{ ...topListPaperSx(theme), mb: 0, overflow: "hidden" }}>
              <TopList
                value={location}
                setIsQuickAutomatedPlaceBooking={setIsQuickAutomatedPlaceBooking}
                displayBookNowButton={
                  (!location.isMobileWorking || mobileWorkingEnabled) && bookingAssistenceActive
                }
                bookButtonColor="secondary"
                bookButtonVariant="contained"
              />
            </Paper>
          </Box>
        ))}
      </Box>

      <IconButton
        size="small"
        color="inherit"
        aria-label={t("_dashboard_previousTopLocation")}
        disabled={!canScrollLeft}
        onClick={() => scrollBySlide(-1)}
        sx={chevronButtonSx(theme, "previous", canScrollLeft)}
        data-testid="dashboard-top-list-peek-chevron-previous"
        data-visible={canScrollLeft ? "true" : "false"}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        color="inherit"
        aria-label={t("_dashboard_nextTopLocation")}
        disabled={!canScrollRight}
        onClick={() => scrollBySlide(1)}
        sx={chevronButtonSx(theme, "next", canScrollRight)}
        data-testid="dashboard-top-list-peek-chevron-next"
        data-visible={canScrollRight ? "true" : "false"}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
};
